import { noop } from "lodash-es"
import { convertSubunitIntoUnit, convertUnitIntoSubunit } from "@oysterhr/common"
import { booleanToTextValue } from "~/app/utils/helpers"
import {
  CalendarDurationType,
  PostTerminationPaymentAmountType,
  RestrictiveCovenants as RestrictiveCovenantsType,
} from "@oysterhr/schema"
import {
  ClientPaymentType,
  DurationOption,
  EmploymentRestrictiveCovenants,
  NonSolicitationOptionsEnum,
  PostTerminationSection,
  RestrictiveCovenantsFormValues,
  ServerPaymentType,
} from "../types"
import { getDurationOptions } from "./optionsSelectInputs"
import { RestrictiveCovenantsRulesFragment } from "~/gql/graphql"

const nonSolicitationOptionsMapClientToServer = (
  restrictiveCovenantsOption?: NonSolicitationOptionsEnum,
) => {
  if (!restrictiveCovenantsOption) {
    return undefined
  }
  return {
    nonSolicitationEmployees: ["employees", "both"].includes(restrictiveCovenantsOption),
    nonSolicitationCustomers: ["customers", "both"].includes(restrictiveCovenantsOption),
  } as Pick<RestrictiveCovenantsType, "nonSolicitationCustomers" | "nonSolicitationEmployees">
}
const nonSolicitationOptionsMapServerToClient = (
  restrictiveCovenants?: RestrictiveCovenantsType | null,
): NonSolicitationOptionsEnum | undefined => {
  const { nonSolicitationCustomers, nonSolicitationEmployees } = (restrictiveCovenants ||
    {}) as RestrictiveCovenantsType
  return nonSolicitationCustomers && nonSolicitationEmployees
    ? NonSolicitationOptionsEnum.Both
    : nonSolicitationCustomers
      ? NonSolicitationOptionsEnum.Customers
      : nonSolicitationEmployees
        ? NonSolicitationOptionsEnum.Employees
        : undefined
}

const durationMapServerToClient = (
  restrictiveCovenants?: RestrictiveCovenantsType | null,
  knowledgeBase?: RestrictiveCovenantsRulesFragment,
) => {
  const { postTerminationDurationMonths } = restrictiveCovenants || {}

  if (!postTerminationDurationMonths) {
    return {
      durationOption: undefined,
      durationCustom: undefined,
    }
  }
  const foundOption = getDurationOptions(noop, knowledgeBase).find(
    option => option.value === `${postTerminationDurationMonths}_${CalendarDurationType.Months}`,
  )

  if (foundOption) {
    return {
      durationOption: foundOption.value,
      durationCustom: undefined,
    }
  }
  return {
    durationOption: "custom" as DurationOption,
    durationCustom: postTerminationDurationMonths,
  }
}

const durationMapClientToServer = (
  durationOption: PostTerminationSection["durationOption"],
  durationCustom: PostTerminationSection["durationCustom"],
) => {
  if (!durationOption) {
    return undefined
  }
  if (durationOption === "custom") {
    return {
      postTerminationDurationMonths: durationCustom,
    }
  }
  const [duration] = durationOption.split("_")
  return {
    postTerminationDurationMonths: parseInt(duration, 10),
  }
}

const paymentMapClientToServer = (
  values: ClientPaymentType,
  currencyCode?: string,
): ServerPaymentType => {
  const { amountFixed, amountPercentage, amountType, paymentFrequency } = values

  if (!amountFixed && !amountPercentage) {
    // Clear payment data when payment is not required
    return {
      postTerminationAmountAbsoluteSubunit: null,
      postTerminationAmountPercentage: null,
      postTerminationPaymentFrequency: null,
    }
  }

  const amountInput = {
    postTerminationAmountAbsoluteSubunit:
      amountType === PostTerminationPaymentAmountType.Flat && amountFixed != null
        ? convertUnitIntoSubunit(+amountFixed, currencyCode || "USD")
        : undefined,
    postTerminationAmountPercentage:
      amountType === PostTerminationPaymentAmountType.PercentageMonthlyGross &&
      amountPercentage != null
        ? parseFloat(`${amountPercentage}` || "0")
        : undefined,
  }

  return {
    ...amountInput,
    postTerminationPaymentFrequency: paymentFrequency,
  }
}
const paymentMapServerToClient = (
  restrictiveCovenants: RestrictiveCovenantsType | null | undefined,
  currencyCode: string | null | undefined,
): ClientPaymentType => {
  const {
    postTerminationAmountAbsoluteSubunit,
    postTerminationAmountPercentage,
    postTerminationPaymentFrequency,
  } = restrictiveCovenants || {}
  const defaultAmountType = PostTerminationPaymentAmountType.Flat

  if (!restrictiveCovenants) {
    return {
      amountFixed: undefined,
      amountPercentage: undefined,
      amountType: defaultAmountType,
      paymentFrequency: undefined,
    }
  }

  const amountInput = {
    amountFixed:
      postTerminationAmountAbsoluteSubunit != null
        ? convertSubunitIntoUnit(postTerminationAmountAbsoluteSubunit, currencyCode || "USD")
        : undefined,
    amountPercentage: postTerminationAmountPercentage ?? undefined,
    amountType: postTerminationAmountPercentage
      ? PostTerminationPaymentAmountType.PercentageMonthlyGross
      : postTerminationAmountAbsoluteSubunit
        ? PostTerminationPaymentAmountType.Flat
        : defaultAmountType, // default value
  }

  const paymentFrequency = postTerminationPaymentFrequency ?? undefined
  return {
    ...amountInput,
    paymentFrequency,
  }
}

export const mapRestrictiveCovenants = {
  serverToClient: {
    nonSolicitationOptions: nonSolicitationOptionsMapServerToClient,
    duration: durationMapServerToClient,
    payment: paymentMapServerToClient,
  },
  clientToServer: {
    nonSolicitationOptions: nonSolicitationOptionsMapClientToServer,
    duration: durationMapClientToServer,
    payment: paymentMapClientToServer,
  },
}

export const getMappedServerToClientValues = (
  employment?: EmploymentRestrictiveCovenants,
  knowledgeBase?: RestrictiveCovenantsRulesFragment,
): RestrictiveCovenantsFormValues => {
  const restrictiveCovenants = (employment?.restrictiveCovenants || {}) as RestrictiveCovenantsType
  const {
    confidentiality,
    postTerminationAreaOfBusiness,
    postTerminationAreaOfBusinessInLocalLanguage,
    nonCompetition,
    nonSolicitation,
    postTerminationAcknowledged,
  } = restrictiveCovenants

  const nonSolicitationOption = mapRestrictiveCovenants.serverToClient.nonSolicitationOptions(
    employment?.restrictiveCovenants,
  )
  const duration = mapRestrictiveCovenants.serverToClient.duration(
    employment?.restrictiveCovenants,
    knowledgeBase,
  )

  const areaOfBusiness = {
    areaOfBusiness: postTerminationAreaOfBusiness ?? undefined,
    areaOfBusinessInLocalLanguage: postTerminationAreaOfBusinessInLocalLanguage ?? undefined,
  }

  const payment = mapRestrictiveCovenants.serverToClient.payment(
    employment?.restrictiveCovenants,
    employment?.salaryCurrencyCode,
  )

  const restrictedArea =
    employment?.restrictiveCovenants?.postTerminationRestrictedArea || undefined
  const additionalAreas = employment?.restrictiveCovenants?.postTerminationAdditionalAreas || []

  return {
    confidentiality: booleanToTextValue(confidentiality),
    postTerminationAreaOfBusiness: postTerminationAreaOfBusiness
      ? postTerminationAreaOfBusiness
      : "",
    postTerminationAreaOfBusinessInLocalLanguage: postTerminationAreaOfBusinessInLocalLanguage
      ? postTerminationAreaOfBusinessInLocalLanguage
      : "",
    nonCompetition: booleanToTextValue(nonCompetition),
    nonSolicitation: booleanToTextValue(nonSolicitation),
    nonSolicitationOption,
    postTerminationSection: {
      ...duration,
      ...payment,
      ...areaOfBusiness,
      restrictedArea,
      additionalAreas,
      acknowledged: postTerminationAcknowledged || undefined,
    },
  }
}
